import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Popper } from 'react-popper';
import { getGlobalStyles } from '@tanium/coreui-mixins';
import { useOverlayMountNode } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion';
import { Arrow } from './Arrow';
import { TooltipContentWrapper } from './TooltipContentWrapper';
import { TooltipProps } from './types';

const StyledTooltipContentWrapper = styled(TooltipContentWrapper)<{ children: React.ReactNode }>(
  getGlobalStyles,
);

const defaultOffsetModifier = {
  name: 'offset',
  options: {
    offset: [0, 8],
  },
};

/**
 * A styled tooltip wrapper to display UI relative to a reference component.
 */
export const AdvancedTooltip = ({
  children,
  modifiers,
  placement: requestedPlacement = 'top',
  positionFixed,
  opacity,
  __UNSAFE_ADDITIONAL_PROPS__,
}: TooltipProps) => {
  const mountNode = useOverlayMountNode();

  const modifiersWithDefaults = useMemo(
    () => [defaultOffsetModifier, ...(modifiers || [])],
    [modifiers],
  );

  const strategy = useMemo(() => (positionFixed ? 'fixed' : undefined), [positionFixed]);

  return !children ? null : (
    <Popper
      modifiers={modifiersWithDefaults}
      placement={requestedPlacement}
      strategy={strategy}
      {...__UNSAFE_ADDITIONAL_PROPS__}
    >
      {({ ref, style, placement, arrowProps }) =>
        ReactDOM.createPortal(
          <StyledTooltipContentWrapper
            ref={ref}
            style={style}
            data-placement={placement}
            opacity={opacity}
          >
            <Arrow
              ref={arrowProps.ref}
              data-placement={placement}
              style={arrowProps.style}
              opacity={opacity}
            />
            {children}
          </StyledTooltipContentWrapper>,
          mountNode,
        )
      }
    </Popper>
  );
};
